import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import PageHero from "../components/site/PageHero";
import { IMAGES } from "../lib/images";
import { useReveal } from "../lib/useReveal";
import { FileCheck2, BarChart3, ShieldCheck } from "lucide-react";

const APPROACH = [
  {
    icon: BarChart3,
    title: "Verified figures only",
    text: "We will only ever publish impact statistics that are supported by records we can evidence. If a number is not verified, it does not appear here.",
  },
  {
    icon: FileCheck2,
    title: "Annual reporting",
    text: "Our annual reports and accounts — once approved by our trustees — will be published openly on our Governance & Transparency page.",
  },
  {
    icon: ShieldCheck,
    title: "Accountable delivery",
    text: "Assistance delivered through partners is documented, so supporters can see what was provided, where, and how it helped.",
  },
];

export default function Impact() {
  const ref = useReveal();
  return (
    <div ref={ref}>
      <Seo
        title="Our Impact"
        path="/our-impact"
        description="How Mount Moriah Relief measures and reports impact: verified figures only, annual published reports, and accountable delivery through responsible partnerships."
      />
      <PageHero
        eyebrow="Our Impact"
        title="Honest measurement, openly reported"
        image={IMAGES.harvest.src}
        imageAlt={IMAGES.harvest.alt}
      >
        <p>
          We are a young organisation, established in 2024. Rather than publish impressive-sounding
          numbers, we are building the discipline of measuring well.
        </p>
      </PageHero>

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl reveal">
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-forest tracking-tight leading-snug">
              Where we are today
            </h2>
            <p className="mt-6 text-charcoal/80 leading-relaxed">
              Mount Moriah Relief was founded in 2024. Our first programmes and partnerships are
              being developed carefully, and we do not yet have verified impact figures to
              publish. We believe credibility matters more than headlines — so this page explains
              how we will measure and report our impact, and it will display confirmed results as
              soon as they exist.
            </p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {APPROACH.map((a) => (
              <article key={a.title} className="reveal bg-white border border-sandstone p-8 lg:p-10">
                <a.icon size={28} strokeWidth={1.5} className="text-gold" aria-hidden="true" />
                <h3 className="mt-6 font-serif text-2xl font-semibold text-charcoal">{a.title}</h3>
                <p className="mt-3 text-sm text-charcoal/75 leading-relaxed">{a.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-forest text-white">
        <div className="max-w-3xl mx-auto px-6 text-center reveal">
          <h2 className="font-serif text-3xl sm:text-4xl font-medium tracking-tight leading-snug">
            Follow our progress
          </h2>
          <p className="mt-5 text-white/80 leading-relaxed">
            Confirmed outcomes, project updates and published reports will appear in our news
            section and on our governance page as our work develops.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/news" className="bg-gold px-8 py-3 font-medium hover:bg-gold-dark transition-colors duration-300" data-testid="impact-news-link">
              News &amp; Stories
            </Link>
            <Link to="/governance" className="border border-white/70 px-8 py-3 font-medium hover:bg-white hover:text-forest transition-colors duration-300" data-testid="impact-governance-link">
              Governance &amp; Transparency
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
